import { computed, ref } from 'vue';
import { useStrapiGet } from 'shared/composables/useStrapiApi';
import { UserStep } from 'src/modules/Step/models/UserStep';
import { UserSection } from 'src/modules/Section/models/UserSection';
import { useRepo } from 'pinia-orm';
import { UserCourse } from 'src/modules/Course/models/UserCourse';
import type { CourseSection } from 'src/modules/Course/models/CourseSection';
import type { Section } from 'src/modules/Section/models/Section';
import type { SectionStep } from 'src/modules/Section/models/SectionStep';
import type { Course } from 'src/modules/Course/models/Course';

export const strapiApiPageLimit =
  +import.meta.env?.VITE_STRAPI_API_PAGE_LIMIT || 25;

const userStepRepo = useRepo(UserStep);
const userSectionRepo = useRepo(UserSection);
const userCourseRepo = useRepo(UserCourse);

/*
 * Scope reconciliation (see the two call sites in
 * fetchStudentAssignmentsForContext).
 *
 * `save()` is an upsert, so narrowing the school/semester/class scope needs an
 * explicit removal of whatever fell out of scope. Doing that with `flush()`
 * *before* the request left every mounted view reading an empty repo for the
 * whole round-trip — long enough for course views to derive state from nothing
 * and persist it. Upserting first and dropping only the remainder keeps each
 * repo on the old set until the new one is in hand.
 *
 * Each call site reconciles against the result set it just asked for, so the
 * list lands on exactly that scope. Never apply this to a follow-up page of a
 * paginated fetch — it would read the earlier pages as stale and delete them.
 */
const stepCount = ref<number | null>(null);
const sectionCount = ref<number | null>(null);
const userCourseCount = ref<number | null>(null);

const stepTotal = ref<number | null>(null);
const sectionTotal = ref<number | null>(null);
const userCourseTotal = ref<number | null>(null);

const courseStoreTotal = ref<number | null>(null);
const courseFavoriteTotal = ref<number | null>(null);

// Criteria-scoped pagination for the standalone section list. When list
// criteria (status/sort/search) are active, the server pages over the FILTERED
// ordering, so the offset must count records fetched under the current
// criteria — the repo count would misalign offsets (cached records from other
// criteria sit at unknown filtered positions) and skip server rows.
// `sectionTotal`/`sectionCount` stay unfiltered: the drawer badge reads them.
const filteredSectionCount = ref(0);
const filteredSectionTotal = ref<number | null>(null);
let activeSectionCriteriaKey = '';

const isLastFilteredSection = computed(
  (): boolean =>
    filteredSectionTotal.value !== null &&
    filteredSectionCount.value >= filteredSectionTotal.value,
);

// Same criteria-scoped accounting for the course and step lists.
const filteredCourseCount = ref(0);
const filteredCourseTotal = ref<number | null>(null);
let activeCourseCriteriaKey = '';

const isLastFilteredUserCourse = computed(
  (): boolean =>
    filteredCourseTotal.value !== null &&
    filteredCourseCount.value >= filteredCourseTotal.value,
);

const filteredStepCount = ref(0);
const filteredStepTotal = ref<number | null>(null);
let activeStepCriteriaKey = '';

const isLastFilteredStep = computed(
  (): boolean =>
    filteredStepTotal.value !== null &&
    filteredStepCount.value >= filteredStepTotal.value,
);

const isLastStep = computed(
  (): boolean =>
    stepCount.value !== null &&
    stepTotal.value !== null &&
    (stepCount.value % strapiApiPageLimit > 0 ||
      stepCount.value >= stepTotal.value),
);

const isLastSection = computed(
  (): boolean =>
    sectionCount.value !== null &&
    sectionTotal.value !== null &&
    (sectionCount.value % strapiApiPageLimit > 0 ||
      sectionCount.value >= sectionTotal.value),
);

const isLastUserCourse = computed(
  (): boolean =>
    userCourseCount.value !== null &&
    userCourseTotal.value !== null &&
    (userCourseCount.value % strapiApiPageLimit > 0 ||
      userCourseCount.value >= userCourseTotal.value),
);

const loading = ref(false);
const error = ref<string[]>([]);

type FetchUserSectionsOptions = {
  sectionDocId?: string;
  sectionDocumentId?: string;
  userCourseId?: number | string;
  offset?: number;
  fetchAll?: boolean;
  /** status/sortBy/search query params — see useListFilters.toListQueryParams */
  criteria?: Record<string, string>;
};

/**
 * Adds an 'order' field to each step in a section's section_step array.
 * Returns a new section object.
 */
export function addSectionStepsOrderFieldToSection(section: Section) {
  if (!section || !Array.isArray(section.section_step)) return section;
  return {
    ...section,
    section_step: section.section_step.map(
      (sectionStep: SectionStep, index: number) => ({
        ...sectionStep,
        order: index,
      }),
    ),
  };
}

/**
 * Adds an 'order' field to each course section in a course's course_sections array,
 * and also adds an 'order' field to each step in each section's section_step array.
 * Returns a new course object.
 */
export function addCourseSectionOrderFieldToCourse(course: Course) {
  if (!course || !Array.isArray(course.course_sections)) return course;
  const { course_sections, ...restCourse } = course;
  return {
    ...restCourse,
    course_sections: course_sections.map(
      (courseSection: CourseSection, index: number) => ({
        ...courseSection,
        order: index,
        section: courseSection.section
          ? addSectionStepsOrderFieldToSection(courseSection.section)
          : courseSection.section,
      }),
    ),
  };
}

export function useAssignments() {
  function getStandaloneSectionCount() {
    return userSectionRepo.where('user_course_id', -1).get().length;
  }

  function normalizeFetchUserSectionsOptions(
    input?: string | FetchUserSectionsOptions,
  ): FetchUserSectionsOptions {
    if (typeof input === 'string') {
      return { sectionDocId: input };
    }

    return input ?? {};
  }

  function recomputeCourseFavoriteTotal() {
    const favoritesCount = userCourseRepo
      .all()
      .filter(
        (userCourse) =>
          userCourse?.is_favorite === true && userCourse?.is_assigned !== false,
      ).length;
    courseFavoriteTotal.value = favoritesCount;
    return favoritesCount;
  }

  async function fetchUserSteps(
    stepFilters?: string,
    criteria?: Record<string, string>,
  ) {
    const hasCriteria = !!criteria && Object.keys(criteria).length > 0;
    const criteriaKey = hasCriteria ? JSON.stringify(criteria) : '';
    if (criteriaKey !== activeStepCriteriaKey) {
      activeStepCriteriaKey = criteriaKey;
      filteredStepCount.value = 0;
      filteredStepTotal.value = null;
    }

    if (hasCriteria ? isLastFilteredStep.value : isLastStep.value) {
      return;
    }

    const {
      execute: executeSteps,
      data: dataSteps,
      error: errorSteps,
    } = useStrapiGet('user-steps-me', {
      // Criteria pages walk the server's filtered ordering — the repo count
      // would misalign offsets there (see fetchUserSections).
      offset: hasCriteria
        ? filteredStepCount.value
        : stepCount?.value
          ? stepCount.value
          : 0,
      filters: stepFilters,
      ...(hasCriteria ? criteria : {}),
    });

    try {
      await executeSteps();
      if (!errorSteps.value && dataSteps.value) {
        userStepRepo.save(dataSteps.value?.[0] ?? []);
        if (hasCriteria) {
          // Skip counter updates for pages of criteria that were replaced
          // mid-flight — see fetchUserSections.
          if (criteriaKey === activeStepCriteriaKey) {
            filteredStepTotal.value = dataSteps.value?.[1] ?? 0;
            filteredStepCount.value += (dataSteps.value?.[0] ?? []).length;
          }
        } else {
          stepTotal.value = dataSteps.value?.[1] ?? 0;
          stepCount.value = userStepRepo.all()?.length ?? 0;
        }
      }
    } catch (err) {
      error.value.push(
        `fetchUserSteps: Error fetching steps: ${err as string}`,
      );
    }
  }

  /**
   * Adds an 'order' field to each step in each userSection's section.section_step array.
   * Returns a new array of userSections.
   */
  function addSectionStepsOrderField(userSections?: UserSection[]) {
    if (!userSections || !Array.isArray(userSections)) return [];

    const newUserSections = userSections.map((userSection) => {
      const { section, ...rest } = userSection;
      if (!section) return userSection;

      return {
        ...rest,
        section: addSectionStepsOrderFieldToSection(section),
      };
    });

    return newUserSections;
  }

  /**
   * Adds an 'order' field to each course section in each userCourse's course.course_sections array.
   * Returns a new array of userCourses.
   */
  function addCourseSectionOrderField(userCourses: UserCourse[]) {
    if (!userCourses || !Array.isArray(userCourses)) return [];

    const newUserCourses = userCourses.map((userCourse: UserCourse) => {
      const { course, ...rest } = userCourse;
      if (!course) return userCourse;

      return {
        ...rest,
        course: addCourseSectionOrderFieldToCourse(course),
      };
    });

    return newUserCourses;
  }

  async function fetchUserSections(input?: string | FetchUserSectionsOptions) {
    const options = normalizeFetchUserSectionsOptions(input);
    const isStandaloneListRequest =
      !options.sectionDocId &&
      !options.sectionDocumentId &&
      options.userCourseId === undefined;

    const criteria = isStandaloneListRequest ? options.criteria : undefined;
    const hasCriteria = !!criteria && Object.keys(criteria).length > 0;
    const criteriaKey = hasCriteria ? JSON.stringify(criteria) : '';

    if (criteriaKey !== activeSectionCriteriaKey) {
      activeSectionCriteriaKey = criteriaKey;
      filteredSectionCount.value = 0;
      filteredSectionTotal.value = null;
    }

    if (isStandaloneListRequest) {
      sectionCount.value = getStandaloneSectionCount();
      if (!options.fetchAll) {
        if (hasCriteria && isLastFilteredSection.value) return;
        if (!hasCriteria && isLastSection.value) return;
      }
    }

    let nextOffset =
      typeof options.offset === 'number'
        ? options.offset
        : isStandaloneListRequest
          ? hasCriteria
            ? filteredSectionCount.value
            : (sectionCount.value ?? 0)
          : 0;

    try {
      do {
        const {
          execute: executeSections,
          data: dataSections,
          error: errorSections,
        } = useStrapiGet('user-sections-me', {
          offset: nextOffset,
          sectionDocId: options.sectionDocId,
          sectionDocumentId: options.sectionDocumentId,
          userCourseId: options.userCourseId,
          ...(hasCriteria ? criteria : {}),
        });

        await executeSections();

        if (errorSections.value || !dataSections.value) {
          break;
        }

        const pageSections = dataSections.value?.[0] ?? [];
        userSectionRepo.save(addSectionStepsOrderField(pageSections));

        if (!isStandaloneListRequest) {
          break;
        }

        if (hasCriteria) {
          // Criteria may have changed while this page was in flight; the
          // records are saved either way (the mirrored predicate controls
          // display), but a stale page must not pollute the pagination
          // accounting of the criteria that replaced it.
          if (criteriaKey === activeSectionCriteriaKey) {
            filteredSectionTotal.value = dataSections.value?.[1] ?? 0;
            filteredSectionCount.value += pageSections.length;
          }
          break;
        }

        sectionTotal.value = dataSections.value?.[1] ?? 0;
        sectionCount.value = getStandaloneSectionCount();

        if (!options.fetchAll) {
          break;
        }

        if (
          pageSections.length === 0 ||
          sectionCount.value >= (sectionTotal.value ?? 0)
        ) {
          break;
        }

        nextOffset = sectionCount.value;
      } while (options.fetchAll);
    } catch (err) {
      error.value.push(
        `fetchUserSections: Error fetching sections: ${err as string}`,
      );
    }
  }

  async function fetchUserCourses(
    courseFilters?: Record<string, unknown>,
    criteria?: Record<string, string>,
  ) {
    const hasCriteria =
      !courseFilters && !!criteria && Object.keys(criteria).length > 0;
    const criteriaKey = hasCriteria ? JSON.stringify(criteria) : '';
    if (criteriaKey !== activeCourseCriteriaKey) {
      activeCourseCriteriaKey = criteriaKey;
      filteredCourseCount.value = 0;
      filteredCourseTotal.value = null;
    }

    userCourseCount.value = userCourseRepo.all()?.length ?? 0;
    if (!courseFilters) {
      if (hasCriteria && isLastFilteredUserCourse.value) return;
      if (!hasCriteria && isLastUserCourse.value) return;
    }

    const { offset, ...restFilters } = courseFilters || {};

    const {
      execute: executeCourses,
      data: dataCourses,
      error: errorCourses,
    } = useStrapiGet('user-courses-me', {
      // Criteria pages walk the server's filtered ordering — the repo count
      // would misalign offsets there (see fetchUserSections).
      offset: hasCriteria
        ? filteredCourseCount.value
        : !isNaN(offset as number)
          ? offset
          : userCourseCount?.value
            ? userCourseCount.value
            : 0,
      filters: restFilters,
      ...(hasCriteria ? criteria : {}),
    });

    try {
      await executeCourses();
      if (!errorCourses.value && dataCourses.value) {
        userCourseRepo.save(addCourseSectionOrderField(dataCourses.value.data));
        if (hasCriteria) {
          // Skip counter updates for pages of criteria that were replaced
          // mid-flight — see fetchUserSections.
          if (criteriaKey === activeCourseCriteriaKey) {
            filteredCourseTotal.value = dataCourses.value.totalCount ?? 0;
            filteredCourseCount.value += (dataCourses.value.data ?? []).length;
          }
        } else if (!courseFilters) {
          userCourseTotal.value = dataCourses.value.totalCount ?? 0;
        }
        userCourseCount.value = userCourseRepo.all()?.length ?? 0;
        recomputeCourseFavoriteTotal();
      } else if (errorCourses.value) {
        // Prevent infinite-scroll retry loops when endpoint fails (e.g. 403).
        userCourseCount.value = userCourseRepo.all()?.length ?? 0;
        userCourseTotal.value = userCourseCount.value;

        const status = (
          errorCourses.value as { response?: { status?: number } }
        )?.response?.status;
        error.value.push(
          `fetchUserCourses: Request failed${status ? ` (${status})` : ''}`,
        );
      }
    } catch (err) {
      userCourseCount.value = userCourseRepo.all()?.length ?? 0;
      userCourseTotal.value = userCourseCount.value;
      error.value.push(
        `fetchUserCourses: Error fetching courses: ${err as string}`,
      );
    }
  }

  async function getCourseStoreTotal() {
    if (courseStoreTotal.value !== null) return courseStoreTotal.value;
    const { execute, data } = useStrapiGet('courses', {
      'filters[is_show_in_store]': true,
      'pagination[limit]': 0,
    });
    try {
      await execute();
      if (data.value) {
        courseStoreTotal.value = data.value.meta?.pagination?.total ?? 0;
      }
    } catch (err) {
      error.value.push(
        `getCourseStoreTotal: Error fetching course store total: ${err as string}`,
      );
    }
    return courseStoreTotal.value;
  }

  function getCourseFavoriteTotal(force = false) {
    if (!force && courseFavoriteTotal.value !== null)
      return courseFavoriteTotal.value;
    recomputeCourseFavoriteTotal();
    return courseFavoriteTotal.value;
  }

  // Fetch all drawer data and counters
  const fetchUserAssignments = async (role: string) => {
    if (role !== 'child' && role !== 'student' && role !== 'teacher') return;
    loading.value = true;
    try {
      if (role === 'teacher') {
        // Teacher preview registrations: courses + standalone sections, no steps.
        await Promise.all([
          fetchUserCourses(),
          fetchUserSections({ fetchAll: true }),
        ]);
      } else if (role === 'student') {
        await fetchUserCourses();
        getCourseFavoriteTotal(true);
      } else {
        await Promise.all([
          fetchUserSteps(),
          fetchUserSections(),
          fetchUserCourses(),
          getCourseStoreTotal(),
        ]);
        getCourseFavoriteTotal(true);
      }
      loading.value = false;
    } catch (err) {
      error.value.push(err as string);
      loading.value = false;
    }
  };
  /**
   * Re-fetches the course/section repos under the school/semester/class context
   * and reconciles them to that scope. Used when the drawer school or semester
   * filter changes for a student.
   */
  const fetchStudentAssignmentsForContext = async (params: {
    schoolDocumentId?: string;
    semesterDocumentId?: string;
    classDocumentId?: string;
  }) => {
    loading.value = true;
    const { schoolDocumentId, semesterDocumentId, classDocumentId } = params;
    const hasContext = !!(
      schoolDocumentId ||
      semesterDocumentId ||
      classDocumentId
    );
    try {
      // Counters reset up front; the repos themselves are reconciled after each
      // response lands (see the scope-reconciliation note above) so no view
      // ever observes them empty mid-request.
      userCourseCount.value = 0;
      userCourseTotal.value = null;
      sectionCount.value = 0;
      sectionTotal.value = null;
      filteredSectionCount.value = 0;
      filteredSectionTotal.value = null;
      activeSectionCriteriaKey = '';
      filteredCourseCount.value = 0;
      filteredCourseTotal.value = null;
      activeCourseCriteriaKey = '';
      filteredStepCount.value = 0;
      filteredStepTotal.value = null;
      activeStepCriteriaKey = '';

      if (hasContext) {
        // Fetch courses filtered by school/semester
        const {
          execute: executeCourses,
          data: dataCourses,
          error: errorCourses,
        } = useStrapiGet('user-courses-me', {
          offset: 0,
          schoolDocumentId,
          semesterDocumentId,
          classDocumentId,
        });
        await executeCourses();
        if (!errorCourses.value && dataCourses.value) {
          // First page only, matching the pre-reconcile behaviour: a context
          // switch resets the list to page 1 and infinite scroll pages on from
          // there.
          const incomingCourses = addCourseSectionOrderField(
            dataCourses.value.data,
          );
          const keptCourseIds = new Set(
            incomingCourses.map((userCourse) => userCourse.id),
          );
          userCourseRepo.save(incomingCourses);
          const staleCourseIds = userCourseRepo
            .all()
            .filter((userCourse) => !keptCourseIds.has(userCourse.id))
            .map((userCourse) => userCourse.id);
          if (staleCourseIds.length) userCourseRepo.destroy(staleCourseIds);

          userCourseTotal.value = dataCourses.value.totalCount ?? 0;
          userCourseCount.value = userCourseRepo.all().length;
          recomputeCourseFavoriteTotal();
        }

        // Fetch sections filtered by school/semester
        const {
          execute: executeSections,
          data: dataSections,
          error: errorSections,
        } = useStrapiGet('user-sections-me', {
          offset: 0,
          schoolDocumentId,
          semesterDocumentId,
          classDocumentId,
          fetchAll: true,
        });
        await executeSections();
        if (!errorSections.value && dataSections.value) {
          // fetchAll: true above, so this is the complete set for the scope.
          const incomingSections = addSectionStepsOrderField(
            dataSections.value?.[0] ?? [],
          );
          const keptSectionIds = new Set(
            incomingSections.map((userSection) => userSection.id),
          );
          userSectionRepo.save(incomingSections);
          const staleSectionIds = userSectionRepo
            .all()
            .filter((userSection) => !keptSectionIds.has(userSection.id))
            .map((userSection) => userSection.id);
          if (staleSectionIds.length) userSectionRepo.destroy(staleSectionIds);

          sectionTotal.value = dataSections.value?.[1] ?? 0;
          sectionCount.value = getStandaloneSectionCount();
        }
      } else {
        // No filter — re-fetch all
        await fetchUserCourses({ offset: 0 });
        await fetchUserSections({ fetchAll: true, offset: 0 });
      }
      getCourseFavoriteTotal(true);
    } catch (err) {
      error.value.push(err as string);
    } finally {
      // Counters follow whatever actually survived in the repos. They are
      // zeroed at the top of this function, but a failed or partial refresh now
      // leaves the previous scope in place — without this resync the badges
      // would read 0 against a list that still has rows.
      userCourseCount.value = userCourseRepo.all().length;
      sectionCount.value = getStandaloneSectionCount();
      loading.value = false;
    }
  };

  function addFavorite() {
    recomputeCourseFavoriteTotal();
    console.log(
      `addFavorite: courseFavoriteTotal is now ${courseFavoriteTotal.value}`,
    );
  }
  function removeFavorite() {
    recomputeCourseFavoriteTotal();
    console.log(
      `removeFavorite: courseFavoriteTotal is now ${courseFavoriteTotal.value}`,
    );
  }

  return {
    loading,
    error,
    stepTotal,
    sectionTotal,
    userCourseTotal,
    isLastStep,
    isLastSection,
    isLastFilteredSection,
    filteredSectionTotal,
    isLastUserCourse,
    isLastFilteredUserCourse,
    filteredCourseTotal,
    isLastFilteredStep,
    filteredStepTotal,
    courseStoreTotal,
    courseFavoriteTotal,
    addFavorite,
    removeFavorite,
    getCourseStoreTotal,
    getCourseFavoriteTotal,
    fetchUserCourses,
    fetchUserSteps,
    fetchUserSections,
    fetchUserAssignments,
    fetchStudentAssignmentsForContext,
  };
}
